import React, { createContext, useContext, useState, ReactNode } from 'react';
import { PostMessageAction } from '../hooks/usePostMessageListener';

interface AgentContextType {
    isAgentLoggedIn: boolean;
    agentToken: any;
    setAgentLoggedIn: (loggedIn: boolean, token?: any) => void;
    handleAgentLogin: (data: PostMessageAction) => void;
    handleAgentLogout: (data: PostMessageAction) => void;
    waitForAgentLogout: () => Promise<void>;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

interface AgentProviderProps {
    children: ReactNode;
}

export const AgentProvider: React.FC<AgentProviderProps> = ({ children }) => {
    const [isAgentLoggedIn, setIsAgentLoggedIn] = useState<boolean>(false);
    const [agentToken, setAgentToken] = useState<any>(null);

    const setAgentLoggedIn = (loggedIn: boolean, token: any = null) => {
        setIsAgentLoggedIn(loggedIn);
        setAgentToken(token);
    };

    const handleAgentLogin = (data: PostMessageAction) => {
        console.log('Agent logged in:', data);
        setIsAgentLoggedIn(true);
        setAgentToken(data.token);
    };

    const handleAgentLogout = (data: PostMessageAction) => {
        console.log('Agent logged out:', data);
        setIsAgentLoggedIn(false);
        setAgentToken(null);
    };

    const waitForAgentLogout = (): Promise<void> => {
        return new Promise<void>((resolve) => {
            // If agent is not logged in, resolve immediately
            if (!isAgentLoggedIn) {
                console.log('Agent not logged in, skipping logout wait');
                resolve();
                return;
            }

            console.log('Waiting for agent logout...');
            let resolved = false;

            const handleMessage = (event: MessageEvent) => {
                try {
                    if (event.data && event.data.action === 'logout' && !resolved) {
                        resolved = true;
                        window.removeEventListener('message', handleMessage);
                        console.log('Agent logout message received');
                        resolve();
                    }
                } catch (error) {
                    console.error('Error in waitForAgentLogout message handler:', error);
                }
            };

            window.addEventListener('message', handleMessage);

            // Timeout after 10 seconds
            setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    window.removeEventListener('message', handleMessage);
                    console.log('Agent logout wait timeout, proceeding...');
                    resolve();
                }
            }, 10000);
        });
    };

    return (
        <AgentContext.Provider
            value={{
                isAgentLoggedIn,
                agentToken,
                setAgentLoggedIn,
                handleAgentLogin,
                handleAgentLogout,
                waitForAgentLogout
            }}
        >
            {children}
        </AgentContext.Provider>
    );
};

export const useAgent = (): AgentContextType => {
    const context = useContext(AgentContext);
    if (context === undefined) {
        throw new Error('useAgent must be used within an AgentProvider');
    }
    return context;
};

export default AgentContext; 